import React, { useEffect, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import {
  Button,
  ButtonType,
  Table
} from 'syngenta-digital-cropwise-react-ui-kit';
import { useStore } from 'components/Store';
import FormSection from 'components/FormSection';
import EmptyTableState from 'components/EmptyTableState';
import PlusIcon from 'assets/plus.svg?react';
import NoProductsSelectedIcon from 'assets/no_products_found.svg';
import ProductListProvider from 'screens/ProductList/context/ProductListProvider';
import AddProductDrawer from 'screens/ProductList/components/AddProductDrawer';
import CreateCustomProductDrawer from 'screens/Applications&WorkOrders/StepsLayout/Components/ProductsStep/components/CreateCustomProductDrawer';
import TableActions from 'screens/Applications&WorkOrders/StepsLayout/Components/TableActions';
import DrawerDimmingOverlay from 'components/DrawerDimmingOverlay';
import FormattedNumber from 'components/FormattedNumber';
import { getAbbreviatedUnit } from 'helpers/unitsAbbreviation';
import resolveUnitPrice from 'screens/Invoices/redesign/helpers/resolveUnitPrice';
import useProduct from 'hooks/useProduct';
import {
  InvoiceFormValues,
  InvoiceFormProduct
} from '../../Constants/invoiceFormikData';
import FillInvoiceProductDrawer, {
  CatalogProduct
} from './components/FillInvoiceProductDrawer';
import './style.css';

// Match the Applications & Work Orders ProductsTable page size (20 rows/page).
const PAGE_SIZE = 20;
const TABLE_SCROLL_HEIGHT = 'calc(100vh - 358px)';
const DRAWER_Z_INDEX = 1000;

const resolveFinancialAccess = (perm?: {
  role?: string;
  financialAccess?: string;
}): string | undefined => {
  if (!perm) return undefined;
  const masked =
    (perm.role === 'Full control' || perm.role === 'View Only') &&
    !Object.hasOwn(perm, 'financialAccess');
  return masked ? 'none' : perm.financialAccess;
};

/**
 * Whether two line items are the same LINE. `lineId` is the real handle; the
 * reference check covers a line that predates it, where matching on an
 * undefined `lineId` would otherwise sweep up every other id-less line.
 */
const isSameLine = (a: InvoiceFormProduct, b: InvoiceFormProduct): boolean =>
  a === b || (!!a.lineId && a.lineId === b.lineId);

interface ProductUnderEdition {
  catalogProduct: CatalogProduct;
  existing?: InvoiceFormProduct;
}

interface ProductCatalogDetails {
  manufacturer?: string;
  productType?: string;
  stdunit?: string;
  registrationNumber?: string;
}

const buildColumns = (
  canWriteCost: boolean,
  openEditDrawer: (p: ProductUnderEdition | null) => void,
  removeProduct: (product: InvoiceFormProduct) => void,
  catalogDetailsByProductId: Record<string, ProductCatalogDetails>
) => {
  const cols: any[] = [
    {
      title: 'Product Name',
      dataIndex: 'productName',
      key: 'name',
      width: 200,
      render: (name: string, record: InvoiceFormProduct) => {
        // In the edit flow `manufacturerName` isn't stamped onto the loaded
        // line item (only the fill drawer sets it), so fall back to the
        // catalog details fetched for every selected product — otherwise the
        // subtitle is blank and the row collapses too short.
        const manufacturer =
          record.manufacturerName ||
          catalogDetailsByProductId[record.productId]?.manufacturer ||
          '';
        return (
          <>
            {/* No fixed maxWidth: let the name use the full cell width before
                truncating with an ellipsis. */}
            <div className="text-neutral-90 font-medium truncate">{name}</div>
            <div className="text-neutral-60 text-xs truncate">
              {manufacturer}
            </div>
          </>
        );
      }
    },
    {
      title: 'Total Invoiced Quantity',
      key: 'totalProduct',
      width: 140,
      align: 'right' as const,
      render: (_: any, record: InvoiceFormProduct) => {
        const { totalQuantityValue: val, totalQuantityUnit: unit } = record;
        return (
          <span className="text-neutral-90">
            {val ? (
              <>
                <FormattedNumber roundedTo={2}>{val}</FormattedNumber>
                <span className="ml-1">{getAbbreviatedUnit(unit)}</span>
              </>
            ) : (
              '---'
            )}
          </span>
        );
      }
    }
  ];

  if (canWriteCost) {
    cols.push(
      {
        title: 'Unit Price',
        key: 'unitPrice',
        width: 130,
        align: 'right' as const,
        render: (_: any, record: InvoiceFormProduct) => {
          const price = resolveUnitPrice({
            storedValue: record.priceValue,
            storedUnit: record.priceUnit,
            quantityValue: record.totalQuantityValue,
            quantityUnit: record.totalQuantityUnit,
            costValue: record.totalCostValue
          });
          return (
            <span className="text-neutral-90">
              {price !== null ? (
                <>
                  <FormattedNumber unit="usCurrency" roundedTo={2}>
                    {price.value}
                  </FormattedNumber>
                  <span>{`/${getAbbreviatedUnit(price.unit)}`}</span>
                </>
              ) : (
                '---'
              )}
            </span>
          );
        }
      },
      {
        title: 'Total Cost',
        dataIndex: 'totalCostValue',
        key: 'cost',
        width: 120,
        align: 'right' as const,
        render: (val: number) => (
          <span className="text-neutral-90">
            {val != null ? (
              <FormattedNumber unit="usCurrency" roundedTo={2}>
                {val}
              </FormattedNumber>
            ) : (
              '---'
            )}
          </span>
        )
      }
    );
  }

  cols.push({
    title: '',
    key: 'actions',
    width: 90,
    align: 'right' as const,
    render: (_: any, record: InvoiceFormProduct) => {
      return (
        <TableActions
          onEdit={() =>
            openEditDrawer({
              catalogProduct: {
                id: record.productId,
                name: record.productName,
                custom: record.custom
              },
              existing: record
            })
          }
          onDelete={() => removeProduct(record)}
          editButtonLabel={`Edit ${record.productName}`}
          deleteButtonLabel={`Delete ${record.productName}`}
        />
      );
    }
  });

  return cols;
};

const renderFooter = (canWriteCost: boolean, totalCost: number) => (
  total: number,
  [start, end]: [number, number]
) => (
  <div className="flex bg-white items-center justify-end">
    {canWriteCost && (
      <div className="flex items-center text-neutral-90 text-base px-3">
        <span className="font-medium">Total Cost:</span>
        <span className="font-semibold ml-1">
          <FormattedNumber unit="usCurrency" roundedTo={2}>
            {totalCost}
          </FormattedNumber>
        </span>
      </div>
    )}
    <span className="text-neutral-60 ml-4">
      {`${start} - ${end} of ${total}`}
    </span>
  </div>
);

const ProductsStep: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<InvoiceFormValues>();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [
    productUnderEdition,
    setProductUnderEdition
  ] = useState<ProductUnderEdition | null>(null);
  const [isAddingProducts, setIsAddingProducts] = useState(false);
  const [isClosingEditDrawer, setIsClosingEditDrawer] = useState(false);
  const [isCreateCustomOpen, setIsCreateCustomOpen] = useState(false);
  const [myProductsReloadTrigger, setMyProductsReloadTrigger] = useState(0);
  // Tracks the pending `closeFillDrawer` unmount timer so a newly-opened
  // drawer can cancel a stale one from a previous close — otherwise
  // reopening within the 300ms window (e.g. Save product A, then
  // immediately edit product B) lets the old timer null out `B`'s state
  // out from under the user.
  const pendingCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const cancelPendingClose = () => {
    if (pendingCloseTimeoutRef.current !== null) {
      clearTimeout(pendingCloseTimeoutRef.current);
      pendingCloseTimeoutRef.current = null;
    }
  };

  useEffect(() => cancelPendingClose, []);

  const {
    state: { loggedInUserOrgPermission }
  } = useStore();

  const memberFinancialAccess = resolveFinancialAccess(
    loggedInUserOrgPermission
  );

  const canWriteCost = memberFinancialAccess === 'WRITE';
  const products = values.invoiceProducts;

  const { fetchProductData } = useProduct();
  const [catalogDetailsByProductId, setCatalogDetailsByProductId] = useState<
    Record<string, ProductCatalogDetails>
  >({});

  // Fetch product type / standard unit / EPA number from the catalog for any
  // selected products we haven't looked up yet, so the "Select Products"
  // drawer's Selected section can display real values instead of blanks.
  useEffect(() => {
    const missing = products.filter(
      (p, index) =>
        !catalogDetailsByProductId[p.productId] &&
        products.findIndex(o => o.productId === p.productId) === index
    );
    if (!missing.length) return undefined;

    let cancelled = false;

    Promise.all(
      missing.map(p =>
        fetchProductData({ productId: p.productId, custom: p.custom }).then(
          (result: { data: ProductCatalogDetails } | null) => ({
            productId: p.productId,
            data: result?.data
          })
        )
      )
    ).then(results => {
      if (cancelled) return;
      const additions: Record<string, ProductCatalogDetails> = {};
      results.forEach(({ productId, data }) => {
        if (data) additions[productId] = data;
      });
      setCatalogDetailsByProductId(prev => ({ ...prev, ...additions }));
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  // Auto-open the "Select Products" drawer on mount when no products have
  // been added yet, matching Applications & Work Orders' ProductsStep
  // behavior of opening the drawer immediately for an empty product list.
  useEffect(() => {
    if (products.length === 0) {
      setIsDrawerOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeProduct = (product: InvoiceFormProduct) => {
    setFieldValue(
      'invoiceProducts',
      products.filter(p => !isSameLine(p, product))
    );
  };

  const openEditDrawer = (product: ProductUnderEdition | null) => {
    cancelPendingClose();
    setIsAddingProducts(false);
    setProductUnderEdition(product);
  };

  // Shared by the Fill drawer's Cancel/Back/X (via `onClose`) and by a
  // completed Save (via `handleSaveProduct`) — Applications calls the same
  // close handler from both paths. When adding a brand-new product, the
  // "Select Products" drawer reopens immediately to cover it (no exit
  // animation needed here); otherwise let the drawer's own slide-out
  // transition finish before unmounting it.
  const closeFillDrawer = () => {
    cancelPendingClose();
    if (isAddingProducts) {
      setIsDrawerOpen(true);
      pendingCloseTimeoutRef.current = setTimeout(() => {
        pendingCloseTimeoutRef.current = null;
        setProductUnderEdition(null);
        setIsAddingProducts(false);
      }, 300);
      return;
    }
    setIsClosingEditDrawer(true);
    pendingCloseTimeoutRef.current = setTimeout(() => {
      pendingCloseTimeoutRef.current = null;
      setProductUnderEdition(null);
      setIsClosingEditDrawer(false);
    }, 300);
  };

  const handleSaveProduct = (product: InvoiceFormProduct) => {
    const edited = productUnderEdition?.existing;
    if (edited) {
      setFieldValue(
        'invoiceProducts',
        products.map(p =>
          isSameLine(p, edited) ? { ...product, lineId: p.lineId } : p
        )
      );
    } else {
      // Every selection adds its own line: the same product can legitimately
      // appear on an invoice more than once (e.g. two purchases at different
      // unit prices), so a matching `productId` never overwrites an existing row.
      setFieldValue('invoiceProducts', [
        ...products,
        { ...product, lineId: crypto.randomUUID() }
      ]);
    }
    closeFillDrawer();
  };

  // Called from the AddProductDrawer: either the "+" on a catalog row or a click
  // on a row in its Selected section. A catalog row always starts a NEW line —
  // including for a product already on the invoice — while a Selected row edits
  // the one line it stands for, addressed by `productApplicationId` (the line's
  // `lineId`), the same per-line handle Applications uses.
  const handleOnEdit = (item: any) => {
    cancelPendingClose();
    if (item.isInUse) {
      const existing = products.find(
        p => (p.lineId ?? p.productId) === item.productApplicationId
      );
      if (existing) {
        setProductUnderEdition({
          catalogProduct: {
            id: existing.productId,
            name: existing.productName,
            custom: existing.custom
          },
          existing
        });
        setIsAddingProducts(false);
        setIsDrawerOpen(false);
        return;
      }
    }

    // My Product List inventory items have productId (catalog UUID) and id (inventory record UUID);
    // catalog search results only have id (catalog UUID).
    const catalogId: string = item.productId ?? item.id ?? '';
    const catalogName: string = item.name ?? item.productName ?? '';
    const isCustom: boolean = item.custom ?? false;

    setProductUnderEdition({
      catalogProduct: { id: catalogId, name: catalogName, custom: isCustom }
    });
    setIsAddingProducts(true);
    setIsDrawerOpen(false);
  };

  // Called when the Create Custom drawer finishes creating a product. Reload the
  // "My Products" list (so the new custom product is discoverable there too) and
  // open the fill drawer to add it as a line item — carrying `custom: true` and
  // the real catalog `productId` (FR-106).
  const handleCustomProductCreationSuccess = (product: {
    id: string;
    name: string;
  }) => {
    cancelPendingClose();
    setIsCreateCustomOpen(false);
    setMyProductsReloadTrigger(prev => prev + 1);
    setIsDrawerOpen(false);
    setProductUnderEdition({
      catalogProduct: { id: product.id, name: product.name, custom: true }
    });
    setIsAddingProducts(true);
  };

  // Called when the "remove" circle is clicked on a selected item in the
  // AddProductDrawer. The drawer hands back the entries that survive, each
  // still carrying its line's `productApplicationId`, so only that one line is
  // dropped even when the same product sits on several.
  const handleSetSelectedProducts = (
    items: Array<{
      id?: string;
      productApplicationId?: string;
      [key: string]: any;
    }> = []
  ) => {
    const remainingLineIds = new Set(
      items.map(i => i.productApplicationId ?? i.id ?? '')
    );
    setFieldValue(
      'invoiceProducts',
      products.filter(p => remainingLineIds.has(p.lineId ?? p.productId))
    );
  };

  // Map InvoiceFormProduct → shape expected by AddProductDrawer
  // selectedProductsList. One entry per LINE (not per product): the drawer keys
  // its Selected rows, its remove action, and the edit callback off
  // `productApplicationId`, so carrying the line's `lineId` there is what lets
  // the same product appear — and be edited — several times over.
  const selectedForDrawer = products.map(p => {
    const details = catalogDetailsByProductId[p.productId];
    return {
      id: p.productId,
      productApplicationId: p.lineId ?? p.productId,
      name: p.productName,
      manufacturer: details?.manufacturer ?? p.manufacturerName ?? '',
      producttype: details?.productType ?? '',
      stdunit: details?.stdunit ?? '',
      registrationnumber: details?.registrationNumber ?? '',
      isInUse: true
    };
  });

  const totalCost = products.reduce(
    (sum, product) => sum + (product.totalCostValue || 0),
    0
  );

  const addProductButton = (
    <Button
      type={ButtonType.primary}
      icon={<PlusIcon fill={products.length > 0 ? '#14803C' : 'white'} />}
      onClick={() => setIsDrawerOpen(true)}
      ghost={products.length > 0}
      style={products.length > 0 ? { background: 'white' } : undefined}
    >
      Add Products
    </Button>
  );

  return (
    <>
      {products.length === 0 ? (
        <div className="flex flex-col w-full bg-white shadow-sm-card rounded-lg">
          <EmptyTableState
            containerClassName="w-full h-full pt-6 pb-10 flex flex-col justify-center items-center"
            title="No Products Selected"
            message="Add products to your Invoice."
            overlayIcon={NoProductsSelectedIcon}
            button={addProductButton}
          />
        </div>
      ) : (
        <div className="flex flex-col w-full" data-testid="products-step">
          <FormSection hasWrapper={false} button={addProductButton}>
            <div className="bg-white rounded-lg shadow-sm-card cwf-invoice-products-table">
              <Table
                columns={buildColumns(
                  canWriteCost,
                  openEditDrawer,
                  removeProduct,
                  catalogDetailsByProductId
                )}
                dataSource={products}
                rowKey={record => record.lineId ?? record.productId}
                scroll={{ y: TABLE_SCROLL_HEIGHT }}
                pagination={{
                  current: currentPage,
                  pageSize: PAGE_SIZE,
                  total: products.length,
                  onChange: setCurrentPage,
                  showSizeChanger: false,
                  showPrevNextJumpers: false,
                  position: ['bottomRight'],
                  showTotal: renderFooter(canWriteCost, totalCost)
                }}
              />
            </div>
          </FormSection>
        </div>
      )}

      {/* Persistent dimming overlay used during drawer-to-drawer transitions
          (Applications parity — FR-155): a single always-rendered overlay
          instead of each drawer's own mask, so swapping between "Select
          Products" and the Fill drawer never flickers brightness. */}
      <DrawerDimmingOverlay
        visible={
          isDrawerOpen || (Boolean(productUnderEdition) && isAddingProducts)
        }
        zIndex={DRAWER_Z_INDEX - 2}
        onClick={
          isDrawerOpen && !productUnderEdition
            ? () => setIsDrawerOpen(false)
            : undefined
        }
      />

      <ProductListProvider>
        <AddProductDrawer
          showProductList
          hideBackButton
          zIndex={DRAWER_Z_INDEX}
          mask={false}
          visible={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          submitButtonText="Done"
          submitButtonType={ButtonType.outline}
          myProductsReloadTrigger={myProductsReloadTrigger}
          selectedProductsList={selectedForDrawer}
          setSelectedProductsList={handleSetSelectedProducts}
          handleOnEdit={handleOnEdit}
          handleAddProductsSubmit={() => setIsDrawerOpen(false)}
          handleOnAddCustomProduct={() => {
            setIsDrawerOpen(false);
            setIsCreateCustomOpen(true);
          }}
        />

        {productUnderEdition && (
          <FillInvoiceProductDrawer
            visible={!isClosingEditDrawer}
            zIndex={DRAWER_Z_INDEX - 1}
            suppressAnimation={isAddingProducts}
            mask={!isAddingProducts}
            catalogProduct={productUnderEdition.catalogProduct}
            existingProduct={productUnderEdition.existing}
            canWriteCost={canWriteCost}
            onSave={handleSaveProduct}
            onClose={closeFillDrawer}
          />
        )}

        {isCreateCustomOpen && (
          <CreateCustomProductDrawer
            visible
            onClose={() => {
              setIsCreateCustomOpen(false);
              setIsDrawerOpen(true);
            }}
            onSuccess={handleCustomProductCreationSuccess}
            zIndex={DRAWER_Z_INDEX - 1}
          />
        )}
      </ProductListProvider>
    </>
  );
};

export default ProductsStep;
